import React, { useEffect, useMemo } from 'react';
import { flatMap, map, size, times, toNumber } from 'lodash';

import { DealAgentChat } from 'src/types/dealAgent';
import styles from './styles.module.scss';
import { useFetchSessions } from 'src/query/dealInsights/dealAgent';
import { Deal } from 'src/types/dealInsights';
import InfiniteScroll from 'src/components/infiniteScroll';
import moment from 'moment';
import Text from 'src/components/text';
import TimeAgo from 'react-timeago';
import cx from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import { DEAL_AGENT_SESSIONS, FETCH } from 'src/query/queryKeys';

interface HistoryProps {
    dealId: string;
    onConversationClick: (id: string) => void;
}

const NoResultsFound = ({ msg, src }: { msg: string, src: string}) => (
    <div className={styles.emptyState}>
        <img src={src} alt="No results" className={styles.emptyStateImg} />
        <p className={styles.emptyStateMsg}>{msg}</p>
    </div>
);

const Placeholder = () => {
    return (
    <div className={styles.placeholderContainer}>
            <div className={cx(styles.placeholder, styles.label)}>
                <span className={cx(styles.line, styles.block)} />
            </div>
            <div className={cx(styles.placeholder, styles.date)}>
                <span className={cx(styles.line, styles.block)} />
            </div>
    </div>)
}

const History: React.FC<HistoryProps> = ({ dealId, onConversationClick }) => {
    const queryClient = useQueryClient();
    const { data: sessions, hasNextPage: hasNextPageEvents, loadMore: loadMoreEvents, isFetching: isFetchingRecords, isFetchingNextPage: isFetchingNextPageEvents, refetch: allRecordsFetch, isRefetching: isRefetchingSessions, isLoading: isLoadingSessions, } = useFetchSessions(dealId);

    // Methods
    useEffect(() => { // when unmounting the component, clear the cached data
        return () => { 
            queryClient.removeQueries({
                queryKey: [DEAL_AGENT_SESSIONS, FETCH, dealId ?? ''],
             }); // clear cached data
        }
    }, [dealId])

    const listOfSessions = useMemo(() => { // list of sessions new sessions are appended only when data is fetched
        const allSessions = flatMap( sessions?.pages || [], (page) => page.sessions);
    
        const groupedMap: Record<string, any> = {};
    
        allSessions.forEach((session) => {
            const timestamp = Number(session.createdDate);
            const createdAt = moment(timestamp);

            const label = createdAt.isSame(moment(), 'day')
                  ? 'Today' : createdAt.isSame(moment().subtract(1, 'day'), 'day')
                  ? 'Yesterday' : 'Older';
            
            if (!groupedMap[label]) {
                groupedMap[label] = {
                    label,
                    sessions: [],
                };
            }
    
            groupedMap[label].sessions.push(session);
        });
    
        return Object.values(groupedMap);
    }, [sessions]);

    return (
        <div className={styles.historyList}>      
           { isLoadingSessions || isRefetchingSessions ? 
              <div className={styles.loaderContainer}> {times(2, (index) => (
                <Placeholder key={index} />))} 
              </div>
             : <> {size(listOfSessions) > 0 ? (
                    <InfiniteScroll
                      loading={isFetchingRecords}
                      hasMore={hasNextPageEvents}
                      loadMore={loadMoreEvents}
                      loader={() => ( 
                        <div className={styles.loaderContainer}> 
                         {times(2, (index) => (
                          <Placeholder key={index} /> ))}
                        </div> )}
                      className={styles.sessionsWrapper}
                    >
                        {map(listOfSessions, (session, index) => (
                         <div key={index} className={styles.content}>
                           <div className={styles.labelContainer}>
                              <p className={styles.label}>{session.label}</p>
                              <span className={styles.divider}></span>
                           </div>
                           <div className={styles.sessionContainer}>
                               {map(session.sessions, (session) => (
                                 <div key={session.id} className={styles.item} onClick={() => onConversationClick(session.id)}>
                                      <div className={styles.question}>
                                       <Text text={session.question} tooltip={{ placement: "auto" }} />
                                      </div>
                                      <div className={styles.occuredAt}>
                                       <TimeAgo date={toNumber(session.createdDate)} live={false} />
                                      </div>
                                 </div> ))}
                            </div>
                          </div> ))}
                    </InfiniteScroll> ) : 
                    <NoResultsFound msg="No sessions found." src={"/images/seek/seekHistory.svg"} />} </> }        
        </div>
    );
};



export default History;
